import { useCallback, useEffect, useState } from 'react';
import useBackoffInterval from './useBackoffInterval';

enum PendingState {
  WAITING_FOR_PENDING = 'WAITING_FOR_PENDING',
  HAS_PENDING = 'HAS_PENDING',
  IDLE = 'IDLE',
}

const useHasPending = (
  hasPendingResult: boolean,
  refetchHasPendingResult: () => void,
  onPendingResolved?: () => void,
  delay: number = 2000,
  backoff: number = 1.2,
): (() => void) => {
  const [pendingState, setPendingState] = useState<PendingState>(PendingState.IDLE);
  const isWaitingForPending = pendingState == PendingState.WAITING_FOR_PENDING;
  const isPending = pendingState == PendingState.HAS_PENDING;

  // Two backoff intervals, one for querying during the 'waiting for pending' phase and one while pending.
  // One interval means the pending check is potentially too coarse
  useBackoffInterval(refetchHasPendingResult, isWaitingForPending, delay, backoff, true);
  useBackoffInterval(refetchHasPendingResult, isPending, delay, backoff, true);

  useEffect(() => {
    switch (pendingState) {
      case PendingState.IDLE:
        if (hasPendingResult) {
          setPendingState(PendingState.WAITING_FOR_PENDING);
        }
        // Idle and no pending result, nothing to poll for
        break;
      case PendingState.WAITING_FOR_PENDING:
        if (hasPendingResult) {
          setPendingState(PendingState.HAS_PENDING);
        }
        // waiting for pending and not pending, keep waiting
        break;
      case PendingState.HAS_PENDING:
        if (!hasPendingResult) {
          setPendingState(PendingState.IDLE);
          onPendingResolved?.();
        }
        // polling for a result but no result yet
        break;
    }
  }, [hasPendingResult, pendingState, onPendingResolved]);

  return useCallback(() => {
    // Check immediately as well as on the interval. The server might resolve an edit inside the first
    // poll window, and a missed window leaves the machine waiting forever without ever refetching.
    refetchHasPendingResult();
    setPendingState(PendingState.WAITING_FOR_PENDING);
  }, [refetchHasPendingResult]);
};

export default useHasPending;
